<template>
  <div class="min-h-screen px-6 py-8 md:px-10">
    <div class="w-full space-y-6">
      <div class="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 class="mt-2 text-3xl text-gray-900">Nova Mensagem Pré Definida</h1>
          <p class="mt-2 max-w-2xl text-sm text-gray-500">
            Crie um modelo reutilizável para respostas mais rápidas e padronizadas.
          </p>
        </div>

        <div class="flex flex-col gap-3 sm:flex-row">
          <button
            type="button"
            class="icon-anim-trigger--back group flex h-11 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-5 font-medium text-gray-700 shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-gray-300 hover:bg-gray-50 hover:shadow-md"
            @click="voltar"
          >
            <svg
              class="icon-anim icon-anim--back h-5 w-5 text-gray-500 group-hover:text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Voltar
          </button>
        </div>
      </div>

      <form class="space-y-6" @submit.prevent="save">
        <div
          class="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm"
        >
          <div class="border-b border-gray-100 px-6 py-5">
            <h2 class="text-lg font-semibold text-gray-900">Conteúdo da mensagem</h2>
            <p class="mt-1 max-w-2xl text-sm text-gray-500">
              Informe título, centro de custo e texto para reutilização em atendimento e
              operações.
            </p>
          </div>

          <div class="p-6">
            <div class="grid grid-cols-1 gap-5 xl:grid-cols-12">
              <!-- INPUT -->
              <div class="flex flex-col gap-1.5 xl:col-span-6">
                <label class="text-sm font-medium text-gray-500">Título*</label>
                <input
                  v-model="form.titulo"
                  type="text"
                  placeholder="Ex: Boas-vindas padrão"
                  :class="inputClass"
                  required
                />
              </div>

              <div class="flex flex-col gap-1.5 xl:col-span-6">
                <label class="text-sm font-medium text-gray-500">Centro de custo*</label>
                <div class="relative group">
                  <select v-model="form.centrocusto" :class="selectClass" required>
                    <option value="">Selecione</option>
                    <option :value="null">Geral</option>
                    <option v-for="c in centroscusto" :key="c._id" :value="c._id">
                      {{ c.nome }}
                    </option>
                  </select>
                  <svg
                    class="select-icon pointer-events-none absolute right-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400 transition-all duration-200 group-focus-within:text-blue-100"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </div>
              </div>

              <div class="flex flex-col gap-1.5 xl:col-span-12">
                <label class="text-sm font-medium text-gray-500"
                  >Texto da mensagem*</label
                >
                <textarea
                  v-model="form.mensagem"
                  rows="8"
                  placeholder="Digite a mensagem que será usada como modelo."
                  :class="textareaClass"
                  required
                />
              </div>

              <!-- CHECKBOX -->
              <label
                class="flex items-center gap-3 rounded-xl border border-gray-200 bg-slate-50/40 px-4 py-3 text-sm text-gray-700 xl:col-span-12"
              >
                <input v-model="form.ativo" type="checkbox" :class="checkboxClass" />
                Mensagem ativa para uso imediato
              </label>
            </div>
          </div>
        </div>

        <div
          v-if="feedback.message"
          class="rounded-2xl border px-4 py-3 text-sm"
          :class="feedbackClass"
        >
          {{ feedback.message }}
        </div>

        <div class="flex flex-col gap-3 sm:flex-row sm:justify-end">
          <button
            type="submit"
            class="h-11 rounded-xl px-6 font-medium text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"
            :style="{ backgroundColor: primaryColor }"
            @mouseenter="$event.target.style.backgroundColor = mixPrimaryColor('#111827', 0.2)"
            @mouseleave="$event.target.style.backgroundColor = primaryColor"
          >
            Salvar mensagem
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { colorMixin } from '@/utils/colorMixin';

export default {
  mixins: [colorMixin],
  data() {
    return {
      centroscusto: [],
      form: {
        titulo: "",
        mensagem: "",
        centrocusto: null,
        ativo: true,
      },
      feedback: {
        message: "",
        type: "success",
      },
      feedbackStyles: {
        success: "border-green-200 bg-green-50 text-green-700",
        warning: "border-amber-200 bg-amber-50 text-amber-700",
      },
      inputClass:
        "h-11 rounded-xl border border-gray-200 bg-white px-4 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100",
      selectClass:
        "h-11 w-full cursor-pointer appearance-none rounded-xl border border-gray-200 bg-white bg-none px-4 pr-10 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100",
      textareaClass:
        "rounded-2xl border border-gray-200 bg-white px-4 py-3 text-gray-700 shadow-sm outline-none transition-all duration-200 focus:border-blue-100 focus:ring-2 focus:ring-blue-100",
      checkboxClass: "h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500",
    };
  },

  computed: {
    feedbackClass() {
      return this.feedbackStyles[this.feedback.type] || this.feedbackStyles.success;
    },
  },

  methods: {
    mostrarFeedback(message, type = "success") {
      this.feedback.message = message;
      this.feedback.type = type;
    },

    voltar() {
      this.$router.push({ path: "/premensagem" });
    },

    async save() {
      if (!this.form.titulo || !this.form.titulo.trim()) {
        this.mostrarFeedback("Preencha o título da mensagem para continuar.", "warning");
        return;
      }

      if (this.form.centrocusto === "" || typeof this.form.centrocusto === "undefined") {
        this.mostrarFeedback("Selecione o centro de custo para continuar.", "warning");
        return;
      }

      if (!this.form.mensagem || !this.form.mensagem.trim()) {
        this.mostrarFeedback("Preencha o texto da mensagem para continuar.", "warning");
        return;
      }

      const method = this.form._id ? "put" : "post";
      const req = await this.$http[method](`/v1/premensagem`, this.form);

      if (req.data.success) {
        this.mostrarFeedback("Mensagem pré definida salva com sucesso.");
        this.$router.push({ path: `/premensagem` });
      } else {
        this.mostrarFeedback(
          req.data.err || "Não foi possível salvar a mensagem.",
          "warning"
        );
      }
    },
  },

  async beforeMount() {
    const id = this.$route.params.id;

    if (id) {
      const req = await this.$http.get(`/v1/premensagem/${id}`);
      this.form = {
        ...this.form,
        ...req.data,
        centrocusto:
          typeof req.data.centrocusto === "undefined" ? "" : req.data.centrocusto,
      };
    }

    const centroscustoReq = await this.$http.post(`/v1/centrocusto/list`, { all: true });
    this.centroscusto = centroscustoReq.data.data;
  },
};
</script>

<style scoped>
.select-icon {
  pointer-events: none;
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  transition: transform 0.2s ease, color 0.2s ease;
}

.group:focus-within .select-icon {
  transform: translateY(-50%) rotate(180deg);
}
</style>
